import {
	Calendar as CalendarIcon,
	Clock,
	MapPin,
	Pen,
	Lightbulb,
} from "lucide-react";
import { TaskListExtension } from "@/components/TaskListExtension";
import type { CalendarEvent } from "@/services/business/calendar";
import type { Task } from "@/stores/calendarStore";

interface ShootingEditorialCardProps {
	event: CalendarEvent;
	tasks: Task[];
	onClick: () => void;
	onOpenEditorialBooking: (event: CalendarEvent) => void;
	onShowTopicsModal: (data: {
		shootingId: string;
		shootingTitle?: string;
		shootingDate?: Date;
	}) => void;
}

export function ShootingEditorialCard({
	event,
	tasks,
	onClick,
	onOpenEditorialBooking,
	onShowTopicsModal,
}: ShootingEditorialCardProps) {
	const isPastEvent = event.date < new Date();

	return (
		<div className="flex flex-col">
			<div
				className={`group bg-white/[0.02] border rounded-lg p-3 transition-all duration-300 border-white/5 hover:border-warning/20 cursor-pointer active:scale-[0.98] ${
					tasks.length > 0 ? "rounded-b-none" : ""
				} ${isPastEvent ? "opacity-50" : ""}`}
				onClick={onClick}
			>
				<div className="flex-col">
					<div className="flex-1 min-w-0">
						<div className="flex items-start justify-between gap-3 mb-1.5">
							<div className="flex items-center gap-2">
								{event.status === "pending" && event.type !== "editorial" && (
									<div className="text-[9px] uppercase tracking-wider text-white/40">
										En attente
									</div>
								)}
							</div>
						</div>

						<h3 className="text-base text-white tracking-tight leading-snug mb-2 line-clamp-2">
							{event.type === "shooting" && event.directorName
								? `Votre tournage avec ${event.directorName}`
								: event.type === "editorial" && event.relatedShootingDate
									? `RDV éditorial pour votre tournage du ${event.relatedShootingDate.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}`
									: event.type === "editorial" && event.pbmName
										? `RDV éditorial avec ${event.pbmName}`
										: event.title}
						</h3>

						{(event.time ||
							event.location ||
							event.type === "editorial" ||
							event.relatedShootingDate ||
							(event.type === "shooting" && event.linkedEditorialBooking)) && (
							<div className="flex items-center gap-2 flex-wrap">
								{event.time && (
									<div className="bg-white/[0.03] rounded-md px-2 py-1 border border-white/10 flex items-center gap-1.5">
										<Clock className="h-3 w-3 text-white/50" />
										<span className="text-xs tracking-wide text-white/50">
											{event.endTime
												? `${event.time} - ${event.endTime}`
												: event.time}
										</span>
									</div>
								)}
								{event.type === "editorial" && event.relatedShootingDate && (
									<div className="bg-white/[0.03] rounded-md px-2 py-1 border border-warning/20 flex items-center gap-1.5">
										<CalendarIcon className="h-3 w-3 text-warning/70" />
										<span className="text-xs tracking-wide text-warning/70">
											Tournage le{" "}
											{event.relatedShootingDate.toLocaleDateString("fr-FR", {
												day: "numeric",
												month: "long",
											})}
										</span>
									</div>
								)}
								{event.type === "shooting" && event.linkedEditorialBooking && (
									<div className="bg-white/[0.03] rounded-md px-2 py-1 border border-warning/20 flex items-center gap-1.5">
										<Pen className="h-3 w-3 text-warning/70" />
										<span className="text-xs tracking-wide text-warning/70">
											RDV éditorial le{" "}
											{event.linkedEditorialBooking.date.toLocaleDateString(
												"fr-FR",
												{
													day: "numeric",
													month: "long",
												},
											)}
										</span>
									</div>
								)}
								{event.type === "shooting" && !event.linkedEditorialBooking && (
									<button
										onClick={(e) => {
											e.stopPropagation();
											onOpenEditorialBooking(event);
										}}
										className="bg-white/[0.03] rounded-md px-3 py-1.5 border border-warning/20 hover:border-warning/40 flex items-center gap-1.5 transition-all active:scale-95"
									>
										<Pen className="h-3 w-3 text-warning/70" />
										<span className="text-xs tracking-wide text-warning/70">
											Planifier un RDV éditorial
										</span>
									</button>
								)}
								{event.type === "shooting" && event.shootingId && (
									<button
										onClick={(e) => {
											e.stopPropagation();
											onShowTopicsModal({
												shootingId: event.shootingId!,
												shootingTitle: event.title,
												shootingDate: event.date,
											});
										}}
										className="bg-white/[0.03] rounded-md px-3 py-1.5 border border-white/10 hover:border-white/30 flex items-center gap-1.5 transition-all active:scale-95"
									>
										<Lightbulb className="h-3 w-3 text-white/50" />
										<span className="text-xs tracking-wide text-white/50">
											Voir les sujets
										</span>
									</button>
								)}
								{event.type === "editorial" && (
									<div className="bg-white/[0.03] rounded-md px-2 py-1 border border-white/10 flex items-center gap-1.5">
										<svg
											className="h-3 w-3 text-white/50"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
										>
											<path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
										</svg>
										<span className="text-xs tracking-wide text-white/50">
											Visioconférence
										</span>
									</div>
								)}
								{event.location && event.type !== "editorial" && (
									<div className="bg-white/[0.03] rounded-md px-2 py-1 border border-white/10 flex items-center gap-1.5">
										<MapPin className="h-3 w-3 text-white/50" />
										<span className="text-xs tracking-wide text-white/50">
											{event.location}
										</span>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</div>

			<TaskListExtension tasks={tasks} eventDate={event.date} />
		</div>
	);
}
